import { Phone, Mail, MapPin, TrainFront, SquareParking, Clock } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import AppointmentForm from "../components/AppointmentForm";
import useSEO from "../hooks/useSEO";

const INFO = [
  {
    icon: Phone,
    label: "Telefono",
    value: "011 19667070",
    href: "tel:01119667070",
    testid: "contatti-info-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: "info@dentalchivasso.it",
    href: "mailto:info@dentalchivasso.it",
    testid: "contatti-info-email",
  },
  {
    icon: MapPin,
    label: "Indirizzo",
    value: "Via Gerbido 15/3, 10034 Chivasso (TO)",
    href: "https://www.google.com/maps?q=Via%20Gerbido%2015%2F3%2C%2010034%20Chivasso%20TO",
    testid: "contatti-info-address",
  },
  {
    icon: Clock,
    label: "Orari",
    value: "Martedì – Sabato · 10:00-13:00 e 14:30-20:00",
    testid: "contatti-info-hours",
  },
];

export default function Contatti() {
  useSEO({
    title: "Contatti | Dentalchivasso — Dentista a Chivasso, Via Gerbido 15/3 · 011 19667070",
    description: "Studio Dentistico Dentalchivasso, Via Gerbido 15/3, Chivasso (TO): a 700m dalla Stazione FS con parcheggio. Aperto da martedì a sabato, 10:00-13:00 e 14:30-20:00. Tel 011 19667070.",
    path: "/contatti",
  });
  return (
    <main>
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-40 pb-14" data-testid="contatti-hero">
        <Eyebrow>Contatti</Eyebrow>
        <MaskedLines
          className="mt-6 font-display font-medium tracking-tight text-ink text-5xl sm:text-6xl leading-[1.04]"
          delay={0.2}
          lines={[
            "Ti aspettiamo,",
            <span key="2" className="italic text-teal">a 700m dalla stazione.</span>,
          ]}
        />
      </section>

      {/* ---- INFO ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20" data-testid="contatti-info-section">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {INFO.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.07} className="h-full">
              <a
                href={c.href || undefined}
                target={c.href && c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={c.testid}
                className={`block rounded-3xl border border-line bg-paper p-7 h-full transition-colors duration-300 ${c.href ? "hover:border-teal/50" : "cursor-default"}`}
              >
                <c.icon size={24} className="text-teal" strokeWidth={1.7} />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-mutedc">{c.label}</p>
                <p className="mt-2 font-semibold text-ink leading-snug">{c.value}</p>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---- ORARI DETTAGLIO ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20" data-testid="contatti-orari-section">
        <Reveal>
          <div className="rounded-3xl border border-line bg-paper p-8 md:p-10 grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal">Orari di apertura</p>
              <p className="mt-3 font-display text-2xl md:text-3xl font-medium text-ink leading-snug">
                Aperti da martedì a sabato, <span className="italic text-teal">mattina e pomeriggio.</span>
              </p>
              <p className="mt-3 text-sm text-mutedc">
                Preferisci un orario diverso? Chiamaci: cerchiamo sempre di venirti incontro.
              </p>
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center justify-between gap-6 border-b border-line pb-3" data-testid="orari-row-mattina">
                <span className="font-semibold text-ink">Martedì – Sabato · mattina</span>
                <span className="text-mutedc">10:00 – 13:00</span>
              </li>
              <li className="flex items-center justify-between gap-6 border-b border-line pb-3" data-testid="orari-row-pomeriggio">
                <span className="font-semibold text-ink">Martedì – Sabato · pomeriggio</span>
                <span className="text-mutedc">14:30 – 20:00</span>
              </li>
              <li className="flex items-center justify-between gap-6" data-testid="orari-row-chiusura">
                <span className="font-semibold text-ink">Lunedì e Domenica</span>
                <span className="text-mutedc">Chiuso</span>
              </li>
            </ul>
          </div>
        </Reveal>
      </section>

      {/* ---- FORM + MAPPA ---- */}
      <section className="bg-teal-mist/50 border-y border-line" data-testid="contatti-form-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-14 items-start">
          <div className="space-y-10">
            <Reveal>
              <Eyebrow>Richiesta appuntamento</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Scrivici, <span className="italic text-teal">ti richiamiamo noi.</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed max-w-lg">
                Compila il modulo: riceverai una conferma con il tuo codice di riferimento
                e ti contatteremo al più presto per fissare la visita.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <AppointmentForm />
            </Reveal>
          </div>

          <Reveal delay={0.15} className="space-y-6">
            <div className="relative rounded-[2rem] overflow-hidden border border-line shadow-soft" data-testid="contatti-esterno-frame">
              <img
                src="/esterno.jpg"
                alt="Lo studio Dentalchivasso visto dal parcheggio, Via Gerbido 15/3 Chivasso"
                className="w-full h-[260px] object-cover"
                data-testid="contatti-esterno-photo"
              />
              <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 text-ivory text-xs font-semibold px-4 py-1.5 backdrop-blur">
                Lo studio dal parcheggio · Via Gerbido 15/3
              </span>
            </div>
            <div className="rounded-[2rem] overflow-hidden border border-line shadow-soft" data-testid="contatti-map-frame">
              <iframe
                title="Mappa — Dentalchivasso, Via Gerbido 15/3 Chivasso"
                src="https://www.google.com/maps?q=Via%20Gerbido%2015%2F3%2C%2010034%20Chivasso%20TO&output=embed"
                className="w-full h-[340px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-3xl border border-line bg-paper p-7" data-testid="contatti-treno-card">
                <TrainFront size={24} className="text-teal" strokeWidth={1.7} />
                <p className="mt-4 font-semibold text-ink">In treno</p>
                <p className="mt-2 text-sm text-mutedc leading-relaxed">
                  Siamo a soli 700 metri dalla Stazione FS di Chivasso: esci dalla stazione e
                  raggiungici a piedi in pochi minuti.
                </p>
              </div>
              <div className="rounded-3xl border border-line bg-paper p-7" data-testid="contatti-auto-card">
                <SquareParking size={24} className="text-teal" strokeWidth={1.7} />
                <p className="mt-4 font-semibold text-ink">In auto</p>
                <p className="mt-2 text-sm text-mutedc leading-relaxed">
                  Ampio parcheggio per i pazienti nello spiazzale davanti allo studio, all'interno
                  dell'area commerciale di Via Gerbido 15/3: ti fermi e entri.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}